import type { ProcessedCourse } from "@/common/course/types";
import type { CourseRun } from "@/common/courseRun/types";
import EnrollLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import PromoBanner from "@/components/PromoBanner";
import type { PromoBannerVariant } from "@/components/PromoBanner/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import type { OcPromoBannerTestResult } from "@/monarch/rules/ocPromoBanner/types";

type OCPromoBannerProps = {
  course: ProcessedCourse;
  courseRun: CourseRun;
};

type ComponentProps = OCPromoBannerProps & {
  variant: PromoBannerVariant;
};

function Component({ course, courseRun, variant }: ComponentProps) {
  return (
    <MonarchDataConsumer<OcPromoBannerTestResult> experimentKey="useOcPromoBanner">
      {(testResult) => {
        const { data } = testResult;
        if (!data) {
          return null;
        }
        return (
          <PromoBanner
            ctaElement={
              <EnrollLink
                course={course}
                courseRun={courseRun}
                name="promo-banner-cta"
                location="promo-banner"
                buttonClass="mb-4"
              />
            }
            description={data.description}
            discount={data.discount}
            discountCode={data.discountCode}
            endDatePrefix={data.endDatePrefix}
            endDate={data.endDate}
            image={data.image}
            learnMoreLink={data.learnMoreLink}
            title={data.title}
            variant={variant}
          />
        );
      }}
    </MonarchDataConsumer>
  );
}

export default function OCPromoBanner({
  courseRun,
  course,
}: OCPromoBannerProps) {
  return (
    <MonarchResolver<OcPromoBannerTestResult>
      experimentKey="useOcPromoBanner"
      variants={{
        control: null,
        ocPromoBannerControl: null,
        ocPromoBannerVariantA: (
          <Component course={course} courseRun={courseRun} variant="slim" />
        ),
        ocPromoBannerVariantB: (
          <Component course={course} courseRun={courseRun} variant="bento" />
        ),
      }}
      fallbackVariant="control"
    />
  );
}
